import { MultiSelectCombobox } from '@synoptic/ui-kit/multi-select-combobox/multi-select-combobox.js';
import { MultiSelectMenuContainer } from '@synoptic/ui-kit/multi-select-combobox/multi-select-group.tsx';
import { useMemo } from 'react';
import { Stream } from '@/__generated__/schema-types';
import { useStreamTier } from '@/components/stream-tiers/use-stream-tier';
import { streamTiers } from '@/constants/stream-tiers';
import { isMatchingStream, isSmartNewsStream } from '../stream-matching';
import { PaginatedMultiSelectResult } from './paginated-multiselect-result';
import { StreamListItem } from './stream-list-item';
import { StreamPill } from './stream-pill';
import { TierStreamsSection } from './tier-streams-section';
import { useTypeaheadStreams } from './use-collection-typeahead';
import {
  useTier1Streams,
  useTierPackageStreams,
} from './use-tier-package-streams';

const isItemDisabled = (item: Stream) => !item.canAddToStreamDeck;

const matchesSearch = (stream: Stream, search: string) =>
  !search ||
  stream.name.toLowerCase().includes(search) ||
  !!stream.label?.toLowerCase().includes(search);

export type TypeaheadMultiselectProps = {
  selectedSources: Stream[];
  errorMessage?: string;
  onSourcesChange: (sources: Stream[]) => void;
  /** Offers only streams with market matching or in Tier 1. */
  matchingSourcesOnly?: boolean;
};
export const TypeaheadMultiselect = ({
  selectedSources,
  errorMessage,
  onSourcesChange,
  matchingSourcesOnly = false,
}: TypeaheadMultiselectProps) => {
  // One source for the menu and the combobox's own list: a row it does not
  // know about looks clickable and does nothing.
  const {
    onValueChange: onStreamValueChange,
    value: searchValue,
    streams,
    isLoading: streamsLoading,
  } = useTypeaheadStreams(
    matchingSourcesOnly ? { polymatcherEnabled: true } : undefined,
  );

  // Tier 1 wins over tier 2; the default streams get no section.
  const tier = useStreamTier();
  const tierPackageId = tier?.id === 'tier3' ? undefined : tier?.packageId;
  const tierPackageStreams = useTierPackageStreams(tierPackageId);
  const {
    streams: tier1Streams,
    ids: tier1StreamIds,
    isLoading: tier1Loading,
  } = useTier1Streams();
  const search = searchValue.trim().toLowerCase();

  const tierStreams = useMemo(
    () =>
      tierPackageStreams.filter(
        (stream) =>
          (!matchingSourcesOnly || isSmartNewsStream(stream, tier1StreamIds)) &&
          !selectedSources.some((selected) => selected.id === stream.id) &&
          matchesSearch(stream, search),
      ),
    [
      tierPackageStreams,
      matchingSourcesOnly,
      tier1StreamIds,
      selectedSources,
      search,
    ],
  );

  // The search only returns matching streams, so Tier 1 streams without
  // matching are added from the package.
  const otherStreams = useMemo(() => {
    if (!streams) {
      return null;
    }
    const tier1Extras = matchingSourcesOnly
      ? tier1Streams.filter(
          (stream) =>
            !isMatchingStream(stream) && matchesSearch(stream, search),
        )
      : [];
    const tierIds = new Set(tierStreams.map((stream) => stream.id));
    return [...(streams as Stream[]), ...tier1Extras].filter(
      (stream) => !tierIds.has(stream.id),
    );
  }, [streams, matchingSourcesOnly, tier1Streams, search, tierStreams]);

  const unselectedOtherStreams = useMemo(
    () =>
      (otherStreams ?? []).filter(
        (item) => !selectedSources.find((v) => v.id === item.id),
      ),
    [otherStreams, selectedSources],
  );

  // The tier rows stay clickable while the search is loading or failed.
  const dropdownItems = useMemo(() => {
    if (!otherStreams && tierStreams.length === 0) {
      return null;
    }
    return [...tierStreams, ...unselectedOtherStreams];
  }, [otherStreams, tierStreams, unselectedOtherStreams]);

  return (
    <MultiSelectCombobox<Stream>
      placeholder={
        matchingSourcesOnly
          ? 'ex. Polymarket News'
          : 'ex. US Federal Court Cases'
      }
      dropdownItems={dropdownItems}
      onInputChange={(inputValue) => {
        onStreamValueChange(inputValue || '');
      }}
      errorMessage={errorMessage}
      label={
        matchingSourcesOnly
          ? `Select streams with market matching or from the ${streamTiers.tier1.name} package`
          : 'Select the streams you want to add to the list'
      }
      selectedItems={selectedSources}
      setSelectedItems={(v) => {
        if (v) {
          onSourcesChange(v);
        }
      }}
      isLoading={streamsLoading}
      isItemSelected={(item) => !!selectedSources.find((v) => v.id === item.id)}
      getItemId={(item) => item.id}
      isItemDisabled={isItemDisabled}
      renderDropdownItems={({ getItemProps }) => {
        const getStreamItemProps: typeof getItemProps = (options) =>
          getItemProps({
            ...options,
            'data-testid': 'stream-list-item',
          });
        const hasTierSection = !!tier && tierStreams.length > 0;

        return (
          <MultiSelectMenuContainer>
            {hasTierSection ? (
              <TierStreamsSection
                title={`${tier.name} package`}
                streams={tierStreams}
                tier1StreamIds={tier1StreamIds}
                getItemProps={getStreamItemProps}
                isItemDisabled={isItemDisabled}
              />
            ) : null}
            {hasTierSection &&
            !streamsLoading &&
            unselectedOtherStreams.length === 0 ? null : (
              <PaginatedMultiSelectResult<Stream>
                emptyPlaceholder="No streams found"
                groupName="All streams"
                showLabel={hasTierSection}
                items={otherStreams ?? []}
                selectedItems={selectedSources}
                getItemProps={getStreamItemProps}
                isItemDisabled={isItemDisabled}
                isInitialLoading={streamsLoading}
                showMaxItemsHint={true}
              >
                {(item) => (
                  <StreamListItem
                    stream={item}
                    inTier1={tier1StreamIds.has(item.id)}
                  />
                )}
              </PaginatedMultiSelectResult>
            )}
          </MultiSelectMenuContainer>
        );
      }}
      renderItemPill={(source, onRemove) => (
        <StreamPill
          stream={source}
          onRemove={onRemove}
          incompatible={
            matchingSourcesOnly &&
            !tier1Loading &&
            !isSmartNewsStream(source, tier1StreamIds)
          }
        />
      )}
    />
  );
};
